import React from 'react';
import classNames from 'classnames';
import { ResponsiveLine } from '@nivo/line';
import { useTranslation } from 'react-i18next';

import './GraphTwoPropsComparison.styles.scss';
import './GraphTwoPropsComparison.styles.responsive.scss';

type GraphTwoPropsComparisonProps<T> = {
  className?: string;
  items: T[];
  keyPair: [keyof T, keyof T];
  colorPair?: [string, string];
  labelFormula: (item: T) => string;
  leftLegendString: string;
  bottomLegendString: string;
  mapKeyToLabel?: Record<keyof T, string>;
};

function GraphTwoPropsComparison<T extends Record<string, any>>(
  props: GraphTwoPropsComparisonProps<T>,
) {
  const {
    className,
    items,
    keyPair,
    labelFormula,
    colorPair = ['hsl(251, 70%, 50%)', 'hsl(356, 70%, 50%)'],
    leftLegendString,
    bottomLegendString,
    mapKeyToLabel,
  } = props;
  const { t } = useTranslation();

  const myData = keyPair.map((key, index) => ({
    id: t(mapKeyToLabel?.[key] || (key as string)),
    color: colorPair[index],
    data: items.map((item) => ({
      x: labelFormula(item),

      y: item[key] as number,
    })),
  }));

  const classes = classNames('graph-two-props-comparison', className);

  return (
    <div className={classes}>
      <ResponsiveLine
        data={myData}
        margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
        xScale={{ type: 'point' }}
        curve="monotoneX"
        yScale={{
          type: 'linear',
          min: 'auto',
          max: 'auto',
          stacked: false,
          reverse: false,
        }}
        yFormat=" >-.2f"
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: bottomLegendString,
          legendOffset: 36,
          legendPosition: 'middle',
          truncateTickAt: 0,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: leftLegendString,
          legendOffset: -50,
          legendPosition: 'middle',
          truncateTickAt: 0,
        }}
        pointSize={10}
        pointColor={{ theme: 'background' }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        pointLabel="data.yFormatted"
        pointLabelYOffset={-12}
        enableTouchCrosshair={true}
        useMesh={true}
        legends={[
          {
            anchor: 'bottom-right',
            direction: 'column',
            justify: false,
            translateX: 100,
            translateY: 0,
            itemsSpacing: 0,
            itemDirection: 'left-to-right',
            itemWidth: 80,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: 'circle',
            symbolBorderColor: 'rgba(0, 0, 0, .5)',
            effects: [
              {
                on: 'hover',
                style: {
                  itemBackground: 'rgba(0, 0, 0, .03)',
                  itemOpacity: 1,
                },
              },
            ],
          },
        ]}
      />
    </div>
  );
}

export default GraphTwoPropsComparison;
